/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { Button, Text, Title } from '@/shared/ui';

/** Shared inline style object for filter text inputs. */
export const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2)',
  fontSize: 'var(--text-meta)',
  fontFamily: 'var(--font-family)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-secondary)',
  color: 'var(--text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
};

/** Section heading with icon and title label. */
export const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Title
    order={4}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      fontSize: 'var(--text-meta)',
      fontWeight: 'var(--font-bold)' as unknown as number,
      color: 'var(--text-primary)',
      margin: 0,
      marginBottom: 'var(--space-2)',
    }}
  >
    <Text as="span" style={{ color: 'var(--seed-base)', display: 'flex' }}>{icon}</Text>
    {title}
  </Title>
);

/** Small label rendered above a form field within the panel. */
export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    as="span"
    style={{
      display: 'block',
      fontSize: 'var(--text-micro)',
      fontWeight: 'var(--font-semibold)' as unknown as number,
      color: 'var(--text-tertiary)',
      marginBottom: 'var(--space-1)',
    }}
  >
    {children}
  </Text>
);

/** Multi-select chip button with active/inactive styling. */
export const ChipBtn: React.FC<{ active: boolean; value: string; onClick: () => void }> = ({ active, value, onClick }) => (
  <Button
    type="button"
    onClick={onClick}
    style={{
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-meta)',
      fontWeight: active ? ('var(--font-semibold)' as unknown as number) : ('var(--font-medium)' as unknown as number),
      fontFamily: 'var(--font-family)',
      cursor: 'pointer',
      transition: 'var(--transition-standard)',
      background: active ? 'color-mix(in srgb, var(--seed-base) 8%, var(--surface-primary))' : 'transparent',
      color: active ? 'var(--seed-base)' : 'var(--text-secondary)',
      border: active ? '1.5px solid color-mix(in srgb, var(--seed-base) 40%, transparent)' : '1px solid var(--border-subtle)',
    }}
  >
    {value}
  </Button>
);

/** Single-select toggle button with highlighted active state. */
export const ToggleBtn: React.FC<{ active: boolean; value: string; onClick: () => void }> = ({ active, value, onClick }) => (
  <Button
    type="button"
    onClick={onClick}
    style={{
      flex: 1,
      padding: 'var(--space-2)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-meta)',
      fontWeight: 'var(--font-semibold)' as unknown as number,
      fontFamily: 'var(--font-family)',
      cursor: 'pointer',
      transition: 'var(--transition-standard)',
      background: active ? 'var(--seed-base)' : 'var(--surface-secondary)',
      color: active ? 'var(--button-primary-text)' : 'var(--text-secondary)',
      border: active ? 'none' : '1px solid var(--border-subtle)',
    }}
  >
    {value}
  </Button>
);

/** Compact quantity-select button (e.g. number of rooms). */
export const QtyBtn: React.FC<{ active: boolean; value: string; onClick: () => void }> = ({ active, value, onClick }) => (
  <Button
    type="button"
    onClick={onClick}
    style={{
      width: 24,
      height: 24,
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-micro)',
      fontWeight: 'var(--font-semibold)' as unknown as number,
      fontFamily: 'var(--font-family)',
      cursor: 'pointer',
      transition: 'var(--transition-standard)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: active ? 'var(--seed-base)' : 'var(--surface-secondary)',
      color: active ? 'var(--button-primary-text)' : 'var(--text-secondary)',
      border: active ? 'none' : '1px solid var(--border-subtle)',
    }}
  >
    {value}
  </Button>
);
